import { useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";

interface DialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  wide?: boolean;
}

/** Native <dialog>: focus trapping, Escape and inert background come for free. */
export function Dialog({ open, onClose, title, children, wide }: DialogProps) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal?.();
    if (!open && dialog.open) dialog.close?.();
  }, [open]);

  return (
    <dialog
      ref={ref}
      className={`dialog${wide ? " dialog--wide" : ""}`}
      aria-labelledby="dialog-title"
      onClose={onClose}
      onCancel={onClose}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      {open && (
        <>
          <h2 id="dialog-title">{title}</h2>
          {children}
        </>
      )}
    </dialog>
  );
}

interface ConfirmProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  body: ReactNode;
  confirmLabel: string;
  pending?: boolean;
  danger?: boolean;
}

export function ConfirmDialog({ open, onClose, onConfirm, title, body, confirmLabel, pending, danger }: ConfirmProps) {
  return (
    <Dialog open={open} onClose={onClose} title={title}>
      <p>{body}</p>
      <div className="actions">
        <button type="button" className="button button--quiet" onClick={onClose}>
          Annuler
        </button>
        <button
          type="button"
          className={`button${danger ? " button--danger" : ""}`}
          onClick={onConfirm}
          disabled={pending}
        >
          {confirmLabel}
        </button>
      </div>
    </Dialog>
  );
}

interface RenameProps {
  open: boolean;
  onClose: () => void;
  onSave: (label: string) => void;
  initial: string;
  companyName: string;
  pending?: boolean;
}

export function RenameDialog({ open, onClose, onSave, initial, companyName, pending }: RenameProps) {
  const [label, setLabel] = useState(initial);
  useEffect(() => {
    if (open) setLabel(initial);
  }, [open, initial]);

  function submit(e: FormEvent) {
    e.preventDefault();
    onSave(label.trim());
  }

  return (
    <Dialog open={open} onClose={onClose} title="Renommer pour vous">
      <form onSubmit={submit}>
        <p>Ce nom n'est visible que par vous. Laissez vide pour afficher « {companyName} ».</p>
        <div className="field">
          <label className="field__label" htmlFor="watch-label">
            Votre nom pour cette entreprise
          </label>
          <input
            id="watch-label"
            className="input"
            maxLength={200}
            value={label}
            autoFocus
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Ex. : Fournisseur emballages"
          />
        </div>
        <div className="actions">
          <button type="button" className="button button--quiet" onClick={onClose}>
            Annuler
          </button>
          <button type="submit" className="button" disabled={pending}>
            Enregistrer le nom
          </button>
        </div>
      </form>
    </Dialog>
  );
}
